<template>
  <form @submit.prevent="$emit('submit')">
    <div class="w-full pb-24">
      <div class="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-950/5">
        <div class="divide-y divide-gray-100">
          <section class="px-6 py-8 sm:px-8">
            <h2 class="mb-5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              Dane podstawowe
            </h2>
            <div class="grid gap-6 md:grid-cols-2">
              <div>
                <label class="form-label">Slug</label>
                <input
                  v-model="form.article.slug"
                  type="text"
                  class="form-input mt-1 w-full font-mono text-sm"
                  required
                >
                <div v-if="errorFor('slug')" class="form-error">{{ errorFor('slug') }}</div>
              </div>
              <div>
                <label class="form-label">Witryna</label>
                <select v-model="form.article.site_context" class="form-input mt-1 w-full" required>
                  <option
                    v-for="option in siteContextOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <div v-if="errorFor('site_context')" class="form-error">{{ errorFor('site_context') }}</div>
              </div>
            </div>
          </section>

          <section class="px-6 py-8 sm:px-8">
            <h2 class="mb-5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              Treść ({{ editingLocale.toUpperCase() }})
            </h2>
            <div class="space-y-6">
              <div>
                <label class="form-label">Tytuł ({{ editingLocale.toUpperCase() }})</label>
                <input
                  v-model="loc.title"
                  type="text"
                  class="form-input mt-1 w-full"
                  :required="editingLocale === 'pl'"
                >
                <div v-if="localeErrorFor('title')" class="form-error">{{ localeErrorFor('title') }}</div>
              </div>

              <div>
                <label class="form-label">Zajawka / excerpt ({{ editingLocale.toUpperCase() }})</label>
                <textarea v-model="loc.excerpt" rows="2" class="form-input mt-1 w-full" />
                <div v-if="localeErrorFor('excerpt')" class="form-error">{{ localeErrorFor('excerpt') }}</div>
              </div>

              <div>
                <label class="form-label">Treść ({{ editingLocale.toUpperCase() }})</label>
                <!-- Separate editor instance per locale so undo history/state don't bleed across tabs. -->
                <TiptapEditor
                  v-if="editingLocale === 'pl'"
                  key="pl"
                  v-model="form.article.data_pl.body_html"
                  class="mt-1"
                />
                <TiptapEditor
                  v-else
                  key="en"
                  v-model="form.article.data_en.body_html"
                  class="mt-1"
                />
                <div v-if="localeErrorFor('body_html')" class="form-error">{{ localeErrorFor('body_html') }}</div>
              </div>
            </div>
          </section>

          <section class="px-6 py-8 sm:px-8">
            <h2 class="mb-5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              Miniatura
            </h2>
            <p class="mb-4 text-xs text-gray-500">
              Obraz na liście aktualności i w podglądzie karty. Nie jest wstawiany do treści WYSIWYG.
            </p>
            <div class="grid gap-6 md:grid-cols-2">
              <FileInput
                v-model="form.article.cover_image"
                :errors="coverImageErrors"
                class="w-full"
                label="Wybierz plik"
                accept="image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png"
              />
              <div v-if="coverPreviewUrl" class="flex flex-col">
                <span class="form-label">Podgląd miniatury</span>
                <div class="mt-2 w-40 overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                  <img
                    :src="coverPreviewUrl"
                    alt="Podgląd okładki"
                    class="aspect-video w-full object-cover"
                  >
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
    <slot />
  </form>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { usePage } from '@inertiajs/vue3';
import TiptapEditor from '@/Shared/TiptapEditor.vue';
import FileInput from '@/Shared/FileInput.vue';

const props = defineProps({
  form: { type: Object, required: true },
  editingLocale: { type: String, required: true },
  siteContextOptions: { type: Array, default: () => [] },
  existingCoverUrl: { type: String, default: null },
});

defineEmits(['submit']);

const page = usePage();
const coverObjectUrl = ref(null);

const loc = computed(() => props.form.article[`data_${props.editingLocale}`]);

const errorFor = (field) => page.props.errors?.[`article.${field}`] || page.props.errors?.[field];
const localeErrorFor = (field) => (
  page.props.errors?.[`article.data_${props.editingLocale}.${field}`]
  || page.props.errors?.[`article.${field}`]
);

const coverImageErrors = computed(() => {
  const errs = page.props.errors || {};
  const raw = errs['article.cover_image'] || errs.cover_image;
  if (!raw) return [];
  return Array.isArray(raw) ? raw : [raw];
});

watch(() => props.form.article.cover_image, () => {
  if (coverObjectUrl.value) {
    URL.revokeObjectURL(coverObjectUrl.value);
    coverObjectUrl.value = null;
  }
});

onBeforeUnmount(() => {
  if (coverObjectUrl.value) {
    URL.revokeObjectURL(coverObjectUrl.value);
  }
});

const coverPreviewUrl = computed(() => {
  if (props.form.article.cover_image instanceof File) {
    if (!coverObjectUrl.value) {
      coverObjectUrl.value = URL.createObjectURL(props.form.article.cover_image);
    }
    return coverObjectUrl.value;
  }
  return props.existingCoverUrl;
});
</script>
